import React, { useState, useEffect } from 'react';
import { useAdminApi } from '../../hooks/useAdminApi';
import {
  X,
  CreditCard,
  AlertCircle,
  Loader2,
  CheckCircle,
  Search,
  Building2,
  IndianRupee
} from 'lucide-react';

const ManualPaymentModal = ({ onClose, onSave }) => {
  const { get, post } = useAdminApi();
  const [step, setStep] = useState(1);
  const [tenants, setTenants] = useState([]);
  const [searchTenant, setSearchTenant] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [formData, setFormData] = useState({
    plan: 'starter',
    amount: '',
    duration: 12,
    paymentMethod: 'bank_transfer',
    transactionId: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const planPrices = {
    starter: { monthly: 999, yearly: 9999 },
    professional: { monthly: 2499, yearly: 24999 },
    enterprise: { monthly: 4999, yearly: 49999 }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    // Auto-calculate amount based on plan and duration
    if (formData.plan && formData.duration) {
      const prices = planPrices[formData.plan];
      const amount = formData.duration >= 12 ? prices.yearly : prices.monthly * formData.duration;
      setFormData(prev => ({ ...prev, amount: amount.toString() }));
    }
  }, [formData.plan, formData.duration]);

  const fetchTenants = async () => {
    try {
      const response = await get('/tenants?limit=100');
      if (response.success) {
        setTenants(response.data || response.tenants || []);
      }
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    }
  };

  const filteredTenants = tenants.filter(t =>
    t.companyName?.toLowerCase().includes(searchTenant.toLowerCase()) ||
    t.email?.toLowerCase().includes(searchTenant.toLowerCase())
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTenantSelect = (tenant) => {
    setSelectedTenant(tenant);
    setFormData(prev => ({ ...prev, plan: tenant.subscription?.plan || tenant.plan || 'starter' }));
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const response = await post('/payments/manual', {
        tenantId: selectedTenant.tenantId,
        plan: formData.plan,
        amount: parseInt(formData.amount),
        method: formData.paymentMethod,
        reference: formData.transactionId,
        notes: formData.notes
      });

      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.error || 'Failed to record payment');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (success) {
      onSave();
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-auto z-10">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-3">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Record Manual Payment</h2>
                <p className="text-sm text-gray-500">
                  {step === 1 ? 'Step 1: Select Tenant' : 'Step 2: Payment Details'}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {success ? (
              // Success View
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Recorded!</h3>
                <p className="text-gray-500 mb-6">
                  The payment has been recorded and the license has been updated.
                </p>

                <div className="bg-green-50 rounded-xl p-4 mb-6 text-left">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-green-700">Tenant</p>
                      <p className="font-semibold text-green-800">{selectedTenant?.companyName}</p>
                    </div>
                    <div>
                      <p className="text-green-700">Amount</p>
                      <p className="font-semibold text-green-800">₹{formData.amount}</p>
                    </div>
                    <div>
                      <p className="text-green-700">Plan</p>
                      <p className="font-semibold text-green-800 capitalize">{formData.plan}</p>
                    </div>
                    <div>
                      <p className="text-green-700">Duration</p>
                      <p className="font-semibold text-green-800">{formData.duration} months</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleClose}
                  className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-colors"
                >
                  Done
                </button>
              </div>
            ) : step === 1 ? (
              // Step 1: Select Tenant
              <div>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search tenant by name or email..."
                    value={searchTenant}
                    onChange={(e) => setSearchTenant(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div className="max-h-64 overflow-y-auto space-y-2">
                  {filteredTenants.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Building2 className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      No tenants found
                    </div>
                  ) : (
                    filteredTenants.map((tenant) => (
                      <button
                        key={tenant._id}
                        onClick={() => handleTenantSelect(tenant)}
                        className="w-full flex items-center p-4 border border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                      >
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-white" />
                        </div>
                        <div className="ml-3 flex-1">
                          <p className="font-medium text-gray-900">{tenant.companyName}</p>
                          <p className="text-sm text-gray-500">{tenant.email}</p>
                        </div>
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs capitalize">
                          {tenant.subscription?.plan || tenant.plan || 'starter'}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : (
              // Step 2: Payment Details
              <form onSubmit={handleSubmit}>
                {/* Selected Tenant */}
                <div className="bg-purple-50 rounded-xl p-4 mb-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div className="ml-3">
                      <p className="font-medium text-purple-900">{selectedTenant?.companyName}</p>
                      <p className="text-sm text-purple-700">{selectedTenant?.email}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="ml-auto text-sm text-purple-600 hover:text-purple-700 font-medium"
                    >
                      Change
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Plan */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Plan</label>
                    <div className="grid grid-cols-3 gap-3">
                      {['starter', 'professional', 'enterprise'].map((plan) => (
                        <button
                          key={plan}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, plan }))}
                          className={`p-3 border-2 rounded-xl text-center transition-all ${
                            formData.plan === plan
                              ? 'border-purple-500 bg-purple-50 text-purple-700'
                              : 'border-gray-200 hover:border-gray-300 text-gray-700'
                          }`}
                        >
                          <span className="font-medium capitalize text-sm">{plan}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Duration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (months)
                    </label>
                    <select
                      name="duration"
                      value={formData.duration}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value={1}>1 month</option>
                      <option value={3}>3 months</option>
                      <option value={6}>6 months</option>
                      <option value={12}>12 months (Yearly)</option>
                      <option value={24}>24 months</option>
                      <option value={36}>36 months</option>
                    </select>
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount (₹)
                    </label>
                    <div className="relative">
                      <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        required
                        min="1"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Method
                    </label>
                    <select
                      name="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cash">Cash</option>
                      <option value="cheque">Cheque</option>
                      <option value="upi">UPI</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Transaction ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transaction ID / Reference
                    </label>
                    <input
                      type="text"
                      name="transactionId"
                      value={formData.transactionId}
                      onChange={handleChange}
                      placeholder="Enter transaction reference..."
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes (Optional)
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={2}
                      placeholder="Any additional notes..."
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full mt-6 flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Recording...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Record Payment
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualPaymentModal;
